import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { MultiSelect } from 'primereact/multiselect';
import { Toast } from 'primereact/toast';
import api from '../../services/api';
import { NewsSimulator, TemplateDialog } from './NewsFeedPreview';
import {
  STATUS_FILTERS,
  TEMPLATE_OPTIONS,
  campaignStatus,
  cityOptions,
  fromLocalInput,
  sortFeed,
  toLocalInput,
} from './newsFeedShared';
import './NewsFeed.css';

const CLOUD_NAME = 'dtxar5b36';
const UPLOAD_PRESET = 'bordergarita_uploads';
const UPLOAD_FOLDER = 'news_feed';
const CITIES = cityOptions();

async function uploadImage(file) {
  const form = new FormData();
  form.append('file', file);
  form.append('upload_preset', UPLOAD_PRESET);
  form.append('folder', UPLOAD_FOLDER);
  const res = await fetch(
    `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`,
    { method: 'POST', body: form }
  );
  const json = await res.json();
  if (!res.ok || !json.secure_url) {
    throw new Error(json.error?.message || 'Upload failed');
  }
  return { url: json.secure_url, publicId: json.public_id || '' };
}

function emptyDraft(priority = 0) {
  return {
    type: 'news',
    title: '',
    titleEn: '',
    body: '',
    bodyEn: '',
    imageUrl: '',
    publicId: '',
    gallery: [],
    cityCodes: [],
    priority,
    gridSpan: 'full',
    templateId: 'news_tpl_1',
    isPinned: false,
    active: true,
    advertiserName: '',
    ctaLabel: '',
    ctaUrl: '',
    internalNotes: '',
    startsAt: '',
    endsAt: '',
  };
}

function draftFromItem(item) {
  return {
    ...emptyDraft(item.priority || 0),
    ...item,
    gallery: item.gallery || [],
    cityCodes: item.cityCodes || [],
    startsAt: toLocalInput(item.startsAt),
    endsAt: toLocalInput(item.endsAt),
    isPinned: Boolean(item.isPinned) && item.type === 'news',
  };
}

function payloadFromDraft(draft) {
  return {
    type: draft.type,
    title: draft.title,
    titleEn: draft.titleEn,
    body: draft.body,
    bodyEn: draft.bodyEn,
    imageUrl: draft.imageUrl,
    publicId: draft.publicId,
    gallery: draft.gallery,
    cityCodes: draft.cityCodes,
    priority: Number(draft.priority) || 0,
    gridSpan: draft.gridSpan,
    templateId: draft.templateId,
    isPinned: draft.type === 'news' && draft.isPinned,
    active: draft.active,
    advertiserName: draft.advertiserName,
    ctaLabel: draft.ctaLabel,
    ctaUrl: draft.ctaUrl,
    internalNotes: draft.internalNotes,
    startsAt: fromLocalInput(draft.startsAt),
    endsAt: fromLocalInput(draft.endsAt),
  };
}

export default function NewsFeedManager() {
  const toast = useRef(null);
  const imageRef = useRef(null);
  const galleryRef = useRef(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(emptyDraft());
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('current');
  const [simCity, setSimCity] = useState('BC001');
  const [simView, setSimView] = useState('grid');
  const [liveOnly, setLiveOnly] = useState(true);
  const [previewItem, setPreviewItem] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/news_feed');
      setItems(data.items || []);
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err?.response?.data?.message || 'Failed to load the feed',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const ordered = useMemo(() => sortFeed(items), [items]);

  const counts = useMemo(() => {
    const tally = { live: 0, scheduled: 0, paused: 0, expired: 0, archived: 0 };
    items.forEach((item) => {
      const status = campaignStatus(item);
      tally[status] = (tally[status] || 0) + 1;
    });
    return tally;
  }, [items]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return ordered.filter((item) => {
      const status = campaignStatus(item);
      if (statusFilter === 'current' && status === 'archived') return false;
      if (statusFilter !== 'current' && statusFilter !== 'all' && status !== statusFilter) {
        return false;
      }
      if (typeFilter !== 'all' && item.type !== typeFilter) return false;
      if (!needle) return true;
      const haystack = [
        item.title,
        item.titleEn,
        item.advertiserName,
        item.internalNotes,
        ...(item.cityCodes || []),
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [ordered, query, statusFilter, typeFilter]);

  const patchDraft = (partial) => setDraft((current) => ({ ...current, ...partial }));

  const onType = (type) => {
    const options = TEMPLATE_OPTIONS[type] || TEMPLATE_OPTIONS.news;
    setDraft((current) => ({
      ...current,
      type,
      templateId: options.some((option) => option.value === current.templateId)
        ? current.templateId
        : options[0].value,
      isPinned: type === 'news' ? current.isPinned : false,
    }));
  };

  const openCreate = () => {
    const nextPriority = ordered.reduce(
      (max, item) => Math.max(max, Number(item.priority) || 0),
      -1
    ) + 1;
    setEditingId(null);
    setDraft(emptyDraft(nextPriority));
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    setDraft(draftFromItem(item));
    setDialogOpen(true);
  };

  const onUpload = async (file, gallery) => {
    if (!file) return;
    setUploading(true);
    try {
      const uploaded = await uploadImage(file);
      if (gallery) {
        setDraft((current) => ({
          ...current,
          gallery: [...(current.gallery || []), uploaded].slice(0, 6),
        }));
      } else {
        patchDraft({ imageUrl: uploaded.url, publicId: uploaded.publicId });
      }
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Upload failed',
        detail: err.message || 'Could not upload',
      });
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!draft.title.trim()) {
      toast.current?.show({ severity: 'warn', summary: 'Title required', detail: 'Add a title.' });
      return;
    }
    if (draft.type === 'ad' && !draft.imageUrl) {
      toast.current?.show({ severity: 'warn', summary: 'Image required', detail: 'Ads need a cover image.' });
      return;
    }
    const start = fromLocalInput(draft.startsAt);
    const end = fromLocalInput(draft.endsAt);
    if (start && end && new Date(end) <= new Date(start)) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Dates',
        detail: 'The end has to be after the start.',
      });
      return;
    }
    if (draft.ctaUrl && !/^https?:\/\//i.test(draft.ctaUrl.trim())) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Link',
        detail: 'The CTA link must start with http:// or https://',
      });
      return;
    }
    setSaving(true);
    try {
      const payload = payloadFromDraft(draft);
      if (editingId) await api.put(`/news_feed/${editingId}`, payload);
      else await api.post('/news_feed', payload);
      setDialogOpen(false);
      await load();
      toast.current?.show({ severity: 'success', summary: 'Saved', detail: 'Feed item saved.' });
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err?.response?.data?.message || 'Failed to save',
      });
    } finally {
      setSaving(false);
    }
  };

  const move = async (item, direction) => {
    const ids = ordered.map((entry) => entry.id);
    const index = ids.indexOf(item.id);
    const swap = index + direction;
    if (index < 0 || swap < 0 || swap >= ids.length) return;
    const next = [...ids];
    const [moved] = next.splice(index, 1);
    next.splice(swap, 0, moved);
    try {
      const { data } = await api.put('/news_feed/reorder', { ids: next });
      setItems(data.items || []);
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err?.response?.data?.message || 'Failed to reorder',
      });
    }
  };

  const duplicate = async (item) => {
    try {
      await api.post(`/news_feed/${item.id}/duplicate`);
      await load();
      toast.current?.show({
        severity: 'success',
        summary: 'Duplicated',
        detail: 'Copy saved as paused. Turn it on when it is ready.',
      });
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err?.response?.data?.message || 'Failed to duplicate',
      });
    }
  };

  const archive = async (item) => {
    try {
      await api.post(`/news_feed/${item.id}/archive`, { archived: !item.archived });
      await load();
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err?.response?.data?.message || 'Failed to archive',
      });
    }
  };

  const remove = (item) => {
    confirmDialog({
      message: `Delete “${item.title}” and its metrics? Archive keeps the history.`,
      header: 'Delete item',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await api.delete(`/news_feed/${item.id}`);
          await load();
        } catch (err) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: err?.response?.data?.message || 'Failed to delete',
          });
        }
      },
    });
  };

  const previewDraft = {
    ...payloadFromDraft(draft),
    id: editingId || 'draft',
    gallery: draft.gallery,
    status: 'live',
  };

  return (
    <div className="news-feed-page">
      <Toast ref={toast} />
      <ConfirmDialog />
      <div className="news-feed-layout">
        <Card>
          <div className="news-feed-header">
            <div>
              <h2>News feed</h2>
              <p>
                News and ads for the app, filtered by city and dates. Empty cities means every city.
              </p>
            </div>
            <Button
              label="Add item"
              icon="pi pi-plus"
              className="p-button-primary primary-button"
              onClick={openCreate}
            />
          </div>
          <div className="news-stats">
            <span className="news-stat">{counts.live} live</span>
            <span className="news-stat">{counts.scheduled} scheduled</span>
            <span className="news-stat">{counts.paused} paused</span>
            <span className="news-stat">{counts.expired} expired</span>
            <span className="news-stat">{counts.archived} archived</span>
          </div>
          <div className="news-feed-toolbar">
            <InputText
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search title, advertiser, notes"
            />
            <Dropdown
              value={typeFilter}
              options={[
                { label: 'News and ads', value: 'all' },
                { label: 'News', value: 'news' },
                { label: 'Ads', value: 'ad' },
              ]}
              onChange={(e) => setTypeFilter(e.value)}
            />
            <Dropdown
              value={statusFilter}
              options={STATUS_FILTERS}
              onChange={(e) => setStatusFilter(e.value)}
            />
          </div>
          {loading ? (
            <p className="news-help">Loading…</p>
          ) : visible.length === 0 ? (
            <p className="news-help">No items match these filters.</p>
          ) : (
            <div className="news-list">
              {visible.map((item) => {
                const status = item.status || campaignStatus(item);
                const index = ordered.findIndex((entry) => entry.id === item.id);
                const pinned = item.isPinned && item.type === 'news';
                const prevPinned =
                  index > 0 &&
                  ordered[index - 1].isPinned &&
                  ordered[index - 1].type === 'news';
                const nextPinned =
                  index >= 0 &&
                  index < ordered.length - 1 &&
                  ordered[index + 1].isPinned &&
                  ordered[index + 1].type === 'news';
                return (
                  <div
                    key={item.id}
                    className={`news-row ${item.archived ? 'is-archived' : ''}`}
                  >
                    {item.imageUrl ? (
                      <img src={item.imageUrl} alt="" />
                    ) : (
                      <div className="news-row-fallback">No image</div>
                    )}
                    <div>
                      <div className="news-row-title">
                        <strong>{item.title}</strong>
                        <span className={`news-badge kind`}>{item.type}</span>
                        <span className={`news-badge ${status}`}>{status}</span>
                        {item.isPinned ? <span className="news-badge live">Pinned</span> : null}
                      </div>
                      <div className="news-row-meta">
                        {item.gridSpan === 'half' ? 'Half' : 'Full'}
                        {' · '}
                        {item.templateId}
                        {item.cityCodes?.length
                          ? ` · ${item.cityCodes.length} cities`
                          : ' · All cities'}
                        {item.advertiserName ? ` · ${item.advertiserName}` : ''}
                      </div>
                      <div className="news-metrics">
                        {item.impressions || 0} views · {item.opens || 0} opens · {item.clicks || 0} clicks
                      </div>
                    </div>
                    <div className="news-row-actions">
                      <Button
                        icon="pi pi-arrow-up"
                        className="p-button-text p-button-rounded"
                        disabled={index <= 0 || pinned !== prevPinned}
                        onClick={() => move(item, -1)}
                      />
                      <Button
                        icon="pi pi-arrow-down"
                        className="p-button-text p-button-rounded"
                        disabled={index < 0 || index >= ordered.length - 1 || pinned !== nextPinned}
                        onClick={() => move(item, 1)}
                      />
                      <Button
                        icon="pi pi-eye"
                        className="p-button-text p-button-rounded"
                        onClick={() => setPreviewItem(item)}
                      />
                      <Button
                        icon="pi pi-pencil"
                        className="p-button-text p-button-rounded"
                        onClick={() => openEdit(item)}
                      />
                      <Button
                        icon="pi pi-copy"
                        className="p-button-text p-button-rounded"
                        onClick={() => duplicate(item)}
                      />
                      <Button
                        icon={item.archived ? 'pi pi-replay' : 'pi pi-inbox'}
                        className="p-button-text p-button-rounded"
                        onClick={() => archive(item)}
                      />
                      <Button
                        icon="pi pi-trash"
                        className="p-button-text p-button-rounded p-button-danger"
                        onClick={() => remove(item)}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
        <NewsSimulator
          items={items}
          cityCode={simCity}
          onCityChange={setSimCity}
          view={simView}
          onViewChange={setSimView}
          liveOnly={liveOnly}
          onLiveOnly={setLiveOnly}
          onOpen={setPreviewItem}
        />
      </div>

      <Dialog
        header={editingId ? 'Edit item' : 'Add item'}
        visible={dialogOpen}
        style={{ width: 'min(860px, 96vw)' }}
        onHide={() => setDialogOpen(false)}
        footer={
          <div className="flex justify-content-end gap-2">
            <Button
              label="Preview"
              icon="pi pi-eye"
              className="p-button-text"
              onClick={() => setPreviewItem(previewDraft)}
            />
            <Button label="Cancel" className="p-button-text" onClick={() => setDialogOpen(false)} />
            <Button
              label="Save"
              icon="pi pi-check"
              className="p-button-primary primary-button"
              loading={saving}
              onClick={save}
            />
          </div>
        }
      >
        <div className="news-form">
          <div className="news-form-row">
            <label>
              Type
              <Dropdown
                value={draft.type}
                options={[
                  { label: 'News', value: 'news' },
                  { label: 'Ad', value: 'ad' },
                ]}
                onChange={(e) => onType(e.value)}
              />
            </label>
            <label>
              Width
              <Dropdown
                value={draft.gridSpan}
                options={[
                  { label: 'Full row', value: 'full' },
                  { label: 'Half row', value: 'half' },
                ]}
                onChange={(e) => patchDraft({ gridSpan: e.value })}
              />
            </label>
            <label>
              Template
              <Dropdown
                value={draft.templateId}
                options={TEMPLATE_OPTIONS[draft.type] || TEMPLATE_OPTIONS.news}
                onChange={(e) => patchDraft({ templateId: e.value })}
              />
            </label>
          </div>
          <label>
            Title
            <InputText value={draft.title} onChange={(e) => patchDraft({ title: e.target.value })} />
          </label>
          <label>
            English title
            <InputText
              value={draft.titleEn}
              onChange={(e) => patchDraft({ titleEn: e.target.value })}
              placeholder="Optional. The app uses Spanish when this is empty."
            />
          </label>
          <label>
            Body
            <InputTextarea
              value={draft.body}
              onChange={(e) => patchDraft({ body: e.target.value })}
              rows={5}
              autoResize
              placeholder={'Blank line for a new paragraph. **bold**. Lines starting with "- " become a list.'}
            />
          </label>
          <label>
            English body
            <InputTextarea
              value={draft.bodyEn}
              onChange={(e) => patchDraft({ bodyEn: e.target.value })}
              rows={3}
              autoResize
            />
          </label>
          <div className="news-field">
            Cover
            <div className="news-upload">
              {draft.imageUrl ? (
                <img src={draft.imageUrl} alt="" />
              ) : (
                <div className="news-upload-empty">No image</div>
              )}
              <input
                ref={imageRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  onUpload(file, false);
                }}
              />
              <Button
                type="button"
                label={uploading ? 'Uploading…' : draft.imageUrl ? 'Replace image' : 'Upload image'}
                icon="pi pi-upload"
                className="p-button-outlined"
                loading={uploading}
                onClick={() => imageRef.current?.click()}
              />
            </div>
          </div>
          <div className="news-field">
            Gallery
            <div className="news-gallery">
              {(draft.gallery || []).map((image) => (
                <figure key={image.url}>
                  <img src={image.url} alt="" />
                  <Button
                    type="button"
                    icon="pi pi-times"
                    className="p-button-rounded p-button-danger p-button-sm"
                    onClick={() =>
                      patchDraft({
                        gallery: draft.gallery.filter((entry) => entry.url !== image.url),
                      })
                    }
                  />
                </figure>
              ))}
            </div>
            <input
              ref={galleryRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                onUpload(file, true);
              }}
            />
            <Button
              type="button"
              label="Add gallery image"
              icon="pi pi-images"
              className="p-button-text"
              disabled={(draft.gallery || []).length >= 6 || uploading}
              onClick={() => galleryRef.current?.click()}
            />
          </div>
          <label>
            Cities
            <MultiSelect
              value={draft.cityCodes}
              options={CITIES}
              onChange={(e) => patchDraft({ cityCodes: e.value || [] })}
              filter
              display="chip"
              placeholder="All cities"
              className="w-full"
            />
          </label>
          <p className="news-help">Leave cities empty to show this piece everywhere.</p>
          <div className="news-form-row">
            <label>
              Starts
              <input
                className="p-inputtext"
                type="datetime-local"
                value={draft.startsAt}
                onChange={(e) => patchDraft({ startsAt: e.target.value })}
              />
            </label>
            <label>
              Ends
              <input
                className="p-inputtext"
                type="datetime-local"
                value={draft.endsAt}
                onChange={(e) => patchDraft({ endsAt: e.target.value })}
              />
            </label>
          </div>
          <div className="news-form-row">
            <label>
              Advertiser
              <InputText
                value={draft.advertiserName}
                onChange={(e) => patchDraft({ advertiserName: e.target.value })}
              />
            </label>
            <label>
              CTA label
              <InputText
                value={draft.ctaLabel}
                onChange={(e) => patchDraft({ ctaLabel: e.target.value })}
                placeholder="Learn more"
              />
            </label>
            <label>
              CTA link
              <InputText
                value={draft.ctaUrl}
                onChange={(e) => patchDraft({ ctaUrl: e.target.value })}
                placeholder="https://"
              />
            </label>
          </div>
          <label>
            Internal notes
            <InputTextarea
              value={draft.internalNotes}
              onChange={(e) => patchDraft({ internalNotes: e.target.value })}
              rows={2}
              autoResize
              placeholder="Deal, invoice, contact. Never shown in the app."
            />
          </label>
          <div className="news-form-row">
            <div className="flex align-items-center gap-2">
              <InputSwitch checked={draft.active} onChange={(e) => patchDraft({ active: Boolean(e.value) })} />
              <span>Active</span>
            </div>
            <div className="flex align-items-center gap-2">
              <InputSwitch
                checked={draft.isPinned}
                disabled={draft.type !== 'news'}
                onChange={(e) => patchDraft({ isPinned: Boolean(e.value) })}
              />
              <span>Pin news to the top</span>
            </div>
          </div>
        </div>
      </Dialog>
      <TemplateDialog
        item={previewItem}
        visible={Boolean(previewItem)}
        onHide={() => setPreviewItem(null)}
      />
    </div>
  );
}
